import React, { ChangeEvent, useCallback, useMemo, useState } from "react";
import { useFormikContext } from "formik";
import styled from "styled-components";

import { Input } from "@components/Input";
import {
  faCheck,
  faEdit,
  faPlus,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DEFAULT_THEME } from "@styles/Theme";

const InlineError = styled.div`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  margin-bottom: 8px;
`;

const EmailContainer = styled.div`
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
  width: 100%;
`;

const EmailRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 10px;
  width: 100%;
`;

const StyledInput = styled(Input)`
  flex: 1;
  box-sizing: border-box;
  height: 40px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

interface EmailFieldProps {
  topicId: number;
  topicName: string;
}

export const EmailField: React.FC<EmailFieldProps> = ({
  topicId,
  topicName,
}) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const [newEmail, setNewEmail] = useState<string>("");
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>("");

  const emails = values.requestTopics?.[topicId]?.additional_emails || [];

  const canAddEmail = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return newEmail.trim() !== "" && emailRegex.test(newEmail.trim());
  }, [newEmail]);

  const canSaveEdit = useMemo(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return editingValue.trim() !== "" && emailRegex.test(editingValue.trim());
  }, [editingValue]);

  const handleNewEmailChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setNewEmail(event.currentTarget.value);
    },
    []
  );

  const handleAddEmail = useCallback(() => {
    if (canAddEmail) {
      const currentEmails = [...emails];
      currentEmails.push(newEmail.trim());
      setFieldValue(
        `requestTopics.${topicId}.additional_emails`,
        currentEmails
      );
      setNewEmail("");
    }
  }, [canAddEmail, newEmail, emails, setFieldValue, topicId]);

  const handleRemoveEmail = useCallback(
    (index: number) => {
      const currentEmails = [...emails];
      currentEmails.splice(index, 1);
      setFieldValue(
        `requestTopics.${topicId}.additional_emails`,
        currentEmails
      );
    },
    [emails, setFieldValue, topicId]
  );

  const handleStartEdit = useCallback((index: number, currentEmail: string) => {
    setEditingIndex(index);
    setEditingValue(currentEmail);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (canSaveEdit && editingIndex !== null) {
      const currentEmails = [...emails];
      currentEmails[editingIndex] = editingValue.trim();
      setFieldValue(
        `requestTopics.${topicId}.additional_emails`,
        currentEmails
      );
      setEditingIndex(null);
      setEditingValue("");
    }
  }, [canSaveEdit, editingIndex, editingValue, emails, setFieldValue, topicId]);

  const handleCancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditingValue("");
  }, []);

  const handleEditChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setEditingValue(event.currentTarget.value);
    },
    []
  );

  return (
    <EmailContainer>
      {/* Add new email row */}
      <Row>
        <StyledInput
          type="email"
          value={newEmail}
          placeholder="Add customer email"
          onChange={handleNewEmailChange}
        />
        <IconButton
          type="button"
          disabled={!canAddEmail}
          onClick={handleAddEmail}
          title="Add email"
        >
          <FontAwesomeIcon
            icon={faPlus}
            data-testid="Add"
            color={DEFAULT_THEME.primaryColor}
          />
        </IconButton>
      </Row>

      {newEmail.trim() !== "" && !canAddEmail && (
        <InlineError>Please enter a valid email address</InlineError>
      )}

      {/* Existing emails */}
      {emails.map((email: string, index: number) => (
        <EmailRow key={index}>
          {editingIndex === index ? (
            <>
              <StyledInput
                type="email"
                value={editingValue}
                onChange={handleEditChange}
                placeholder="Edit email address"
              />
              <IconButton
                type="button"
                disabled={!canSaveEdit}
                onClick={handleSaveEdit}
                title="Save changes"
              >
                <FontAwesomeIcon
                  icon={faCheck}
                  data-testid="Save"
                  color={DEFAULT_THEME.primaryColor}
                />
              </IconButton>
              <IconButton
                type="button"
                onClick={handleCancelEdit}
                title="Cancel edit"
              >
                <FontAwesomeIcon icon={faTimes} data-testid="Cancel" />
              </IconButton>
            </>
          ) : (
            <>
              <StyledInput
                type="email"
                value={email}
                disabled={true}
                placeholder="Email address"
              />
              <IconButton
                type="button"
                onClick={() => handleStartEdit(index, email)}
                title="Edit email"
              >
                <FontAwesomeIcon
                  icon={faEdit}
                  data-testid="Edit"
                  color={DEFAULT_THEME.white}
                />
              </IconButton>
              <IconButton
                type="button"
                onClick={() => handleRemoveEmail(index)}
                title="Remove email"
              >
                <FontAwesomeIcon
                  icon={faTrash}
                  data-testid="Remove"
                  color={DEFAULT_THEME.quaternaryColor}
                />
              </IconButton>
            </>
          )}
        </EmailRow>
      ))}

      {editingValue.trim() !== "" && !canSaveEdit && editingIndex !== null && (
        <InlineError>Please enter a valid email address</InlineError>
      )}
    </EmailContainer>
  );
};
